import { Component, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs';
import Swal from 'sweetalert2';


import {
  UsuariosService,
  Usuario,
  UsuarioParceiroRequest,
  UsuarioParceiroDetalhe,
} from '../../../core/services/usuarios.service';

interface FormUsuario {
  nome: string;
  telefone: string;
  email: string;
  dataNascimento: string;
  possuiLimitacaoFisica: boolean;
  informacaoMobilidade: string;
  usarDadosParceiro: boolean;
  emergenciaNome: string;
  emergenciaTelefone: string;
  emergenciaEmail: string;
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './usuarios.html',
  styleUrl: './usuarios.css',
})
export class Usuarios implements OnInit {
  private api = inject(UsuariosService);
  private cdr = inject(ChangeDetectorRef);

  items: Usuario[] = [];
  carregandoDetalhe = false;

  erro = '';
  sucesso = '';

  mostrar = false;
  salvando = false;
  carregando = false;
  excluindo = false;

  editando: string | null = null;

  form: FormUsuario = this.formVazio();

  private formVazio(): FormUsuario {
    return {
      nome: '',
      telefone: '',
      email: '',
      dataNascimento: '',
      possuiLimitacaoFisica: false,
      informacaoMobilidade: '',
      usarDadosParceiro: true,
      emergenciaNome: '',
      emergenciaTelefone: '',
      emergenciaEmail: '',
    };
  }

  ngOnInit(): void {
    this.carregar();
  }

  carregar(): void {
    if (this.carregando) return;

    this.carregando = true;
    this.erro = '';

    this.api
      .listar()
      .pipe(
        finalize(() => {
          this.carregando = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (usuarios) => {
          this.items = usuarios ?? [];
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Erro ao listar usuários:', error);
          this.erro = this.mensagemErro(error, 'Não foi possível carregar os usuários.');
          this.cdr.detectChanges();
        },
      });
  }

  novo(): void {
    this.editando = null;
    this.form = this.formVazio();
    this.erro = '';
    this.sucesso = '';
    this.mostrar = true;
    this.cdr.detectChanges();
  }

  editar(usuario: Usuario): void {
    if (this.salvando || this.excluindo || this.carregandoDetalhe) {
      return;
    }

    this.carregandoDetalhe = true;
    this.erro = '';
    this.sucesso = '';
    this.mostrar = false;
    this.editando = null;

    this.api
      .buscarDetalhe(usuario.usuarioUuid)
      .pipe(
        finalize(() => {
          this.carregandoDetalhe = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (detalhe: UsuarioParceiroDetalhe) => {
          const nascimento = detalhe.dataNascimento
            ? detalhe.dataNascimento.split('-').reverse().join('/')
            : '';

          this.form = {
            nome: detalhe.nome ?? '',
            telefone: this.formatarTelefone(detalhe.telefone ?? ''),
            email: detalhe.email ?? '',
            dataNascimento: nascimento,

            possuiLimitacaoFisica: detalhe.possuiLimitacaoFisica ?? false,

            informacaoMobilidade: detalhe.informacaoMobilidade ?? '',

            usarDadosParceiro: detalhe.emergencia?.usarDadosParceiro ?? true,

            emergenciaNome: detalhe.emergencia?.nome ?? '',

            emergenciaTelefone: this.formatarTelefone(detalhe.emergencia?.telefone ?? ''),

            emergenciaEmail: detalhe.emergencia?.email ?? '',
          };

          this.editando = detalhe.usuarioUuid;
          this.mostrar = true;

          this.cdr.detectChanges();

          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        },

        error: (error: HttpErrorResponse) => {
          console.error('Erro ao buscar detalhes do usuário:', error);

          this.erro = this.mensagemErro(error, 'Não foi possível carregar os dados do usuário.');

          this.cdr.detectChanges();

          void Swal.fire({
            icon: 'error',
            title: 'Erro ao editar usuário',
            text: this.erro,
            confirmButtonColor: '#064F82',
          });
        },
      });
  }

  cancelar(): void {
    if (this.salvando) return;

    this.mostrar = false;
    this.editando = null;
    this.form = this.formVazio();
    this.cdr.detectChanges();
  }

  somenteNumeros(valor: string): string {
    return (valor || '').replace(/\D/g, '');
  }

  private normalizarTelefone(valor: string): string | null {
    const numeros = this.somenteNumeros(valor);

    // Obrigatório: 55 + DDD + celular de 9 dígitos
    if (/^55[1-9][0-9]9[0-9]{8}$/.test(numeros)) {
      return numeros;
    }

    return null;
  }

  formatarTelefone(valor: string): string {
    const n = this.somenteNumeros(valor).slice(0, 13);

    if (!n) return '';

    if (n.length > 11) {
      // DDI 55 + DDD + número
      const ddi = n.slice(0, 2);
      const ddd = n.slice(2, 4);
      const numero = n.slice(4);

      return `+${ddi} (${ddd}) ${numero.slice(0, 5)}${
        numero.length > 5 ? '-' + numero.slice(5) : ''
      }`;
    }

    if (n.length <= 2) return `(${n}`;

    const ddd = n.slice(0, 2);
    const numero = n.slice(2);

    if (numero.length <= 4) {
      return `(${ddd}) ${numero}`;
    }

    const tamanhoPrimeiraParte = numero.length > 8 ? 5 : 4;

    return `(${ddd}) ${numero.slice(0, tamanhoPrimeiraParte)}-${numero.slice(
      tamanhoPrimeiraParte,
    )}`;
  }

  mascaraTelefoneUsuario(valor: string): void {
    this.form.telefone = this.formatarTelefone(valor);
  }

  mascaraTelefoneEmergencia(valor: string): void {
    this.form.emergenciaTelefone = this.formatarTelefone(valor);
  }

  mascaraData(valor: string): void {
    const n = this.somenteNumeros(valor).slice(0, 8);

    if (n.length <= 2) {
      this.form.dataNascimento = n;
    } else if (n.length <= 4) {
      this.form.dataNascimento = `${n.slice(0, 2)}/${n.slice(2)}`;
    } else {
      this.form.dataNascimento = `${n.slice(0, 2)}/${n.slice(2, 4)}/${n.slice(4)}`;
    }
  }

  private converterData(valor: string): string | null {
    if (!valor.trim()) return null;

    const partes = valor.split('/');

    if (partes.length !== 3) return null;

    const [dia, mes, ano] = partes.map(Number);

    if (!dia || !mes || !ano || ano < 1900) return null;

    const data = new Date(ano, mes - 1, dia);

    if (data.getFullYear() !== ano || data.getMonth() !== mes - 1 || data.getDate() !== dia) {
      return null;
    }

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    if (data > hoje) return null;

    return `${ano.toString().padStart(4, '0')}-${mes
      .toString()
      .padStart(2, '0')}-${dia.toString().padStart(2, '0')}`;
  }

  private emailValido(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  private async avisar(mensagem: string): Promise<void> {
    await Swal.fire({
      icon: 'warning',
      title: 'Verifique os dados',
      text: mensagem,
      confirmButtonText: 'Entendi',
      confirmButtonColor: '#064F82',
    });
  }

  private mensagemErro(error: HttpErrorResponse, padrao: string): string {
    const body = error.error;

    if (typeof body === 'string' && body.trim()) {
      return body;
    }

    if (body && typeof body === 'object') {
      if (typeof body.message === 'string' && body.message) {
        return body.message;
      }

      if (typeof body.detail === 'string' && body.detail) {
        return body.detail;
      }

      if (typeof body.error === 'string' && body.error) {
        return body.error;
      }
    }

    return padrao;
  }

  salvar(): void {
    if (this.salvando) return;

    const nome = this.form.nome.trim();
    const telefone = this.normalizarTelefone(this.form.telefone);
    const email = this.form.email.trim();

    if (!nome) {
      void this.avisar('Informe o nome do usuário.');
      return;
    }

    if (!telefone) {
      void this.avisar(
        'Informe o telefone completo com DDI 55 + DDD + número. Exemplo: +55 (00) 00000-0000.'
      );
      return;
    }

    if (email && !this.emailValido(email)) {
      void this.avisar('Informe um e-mail válido.');
      return;
    }

    const nascimento = this.converterData(this.form.dataNascimento);

    if (this.form.dataNascimento.trim() && !nascimento) {
      void this.avisar('Informe uma data de nascimento válida.');
      return;
    }

    if (!this.form.usarDadosParceiro) {
      if (!this.form.emergenciaNome.trim()) {
        void this.avisar('Informe o nome do contato de emergência.');
        return;
      }

      const telefoneEmergencia = this.normalizarTelefone(this.form.emergenciaTelefone);

      if (!telefoneEmergencia) {
        void this.avisar(
          'Informe o telefone de emergência completo com DDI 55. Exemplo: 5562993694073.',
        );
        return;
      }

      const emailEmergencia = this.form.emergenciaEmail.trim();

      if (emailEmergencia && !this.emailValido(emailEmergencia)) {
        void this.avisar('Informe um e-mail válido para o contato de emergência.');
        return;
      }
    }

    const dados: UsuarioParceiroRequest = {
      nome,
      telefone,
      email: email || null,
      dataNascimento: nascimento,
      possuiLimitacaoFisica: this.form.possuiLimitacaoFisica,
      informacaoMobilidade: this.form.possuiLimitacaoFisica
        ? this.form.informacaoMobilidade.trim()
        : '',
      emergencia: {
        usarDadosParceiro: this.form.usarDadosParceiro,
        nome: this.form.usarDadosParceiro ? null : this.form.emergenciaNome.trim(),
        telefone: this.form.usarDadosParceiro
          ? null
          : this.normalizarTelefone(this.form.emergenciaTelefone),
        email: this.form.usarDadosParceiro ? null : this.form.emergenciaEmail.trim() || null,
      },
    };

    this.salvando = true;
    this.erro = '';
    this.sucesso = '';

    const editandoUsuario = !!this.editando;

    const requisicao = this.editando
      ? this.api.atualizar(this.editando, dados)
      : this.api.criar(dados);

    requisicao
      .pipe(
        finalize(() => {
          this.salvando = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: () => {
          this.mostrar = false;
          this.editando = null;
          this.form = this.formVazio();

          this.sucesso = editandoUsuario
            ? 'Usuário atualizado com sucesso.'
            : 'Usuário cadastrado com sucesso.';

          void Swal.fire({
            icon: 'success',
            title: editandoUsuario ? 'Usuário atualizado!' : 'Usuário cadastrado!',
            text: this.sucesso,
            confirmButtonColor: '#064F82',
            timer: 2500,
            timerProgressBar: true,
          });

          this.carregar();
        },
        error: (error: HttpErrorResponse) => {
          console.error('Erro ao salvar usuário:', error);

          this.erro = this.mensagemErro(error, 'Não foi possível salvar o usuário.');

          this.cdr.detectChanges();

          void Swal.fire({
            icon: 'error',
            title: 'Erro ao salvar',
            text: this.erro,
            confirmButtonColor: '#064F82',
          });
        },
      });
  }

  async excluir(usuario: Usuario): Promise<void> {
    if (this.excluindo || this.salvando) return;

    if (!usuario.ativo) {
      await this.avisar('Este usuário já está inativo.');
      return;
    }

    const resultado = await Swal.fire({
      icon: 'warning',
      title: 'Inativar usuário?',
      text: `Deseja inativar ${usuario.nome}? O plano será preservado no histórico.`,
      showCancelButton: true,
      confirmButtonText: 'Sim, inativar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#064F82',
      reverseButtons: true,
    });

    if (!resultado.isConfirmed) return;

    this.excluindo = true;

    this.api
      .inativar(usuario.usuarioUuid)
      .pipe(
        finalize(() => {
          this.excluindo = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: () => {
          void Swal.fire({
            icon: 'success',
            title: 'Usuário inativado!',
            confirmButtonColor: '#064F82',
            timer: 2000,
            timerProgressBar: true,
          });

          this.carregar();
        },
        error: (error: HttpErrorResponse) => {
          void Swal.fire({
            icon: 'error',
            title: 'Erro ao inativar',
            text: this.mensagemErro(error, 'Não foi possível inativar o usuário.'),
            confirmButtonColor: '#064F82',
          });
        },
      });
  }

  async reativar(usuario: Usuario): Promise<void> {
    if (this.excluindo || this.salvando) return;

    const resultado = await Swal.fire({
      icon: 'question',
      title: 'Reativar usuário?',
      text: `Deseja reativar ${usuario.nome}? A reativação depende da validade do plano anterior e de vaga disponível.`,
      showCancelButton: true,
      confirmButtonText: 'Sim, reativar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#064F82',
    });

    if (!resultado.isConfirmed) return;

    this.excluindo = true;

    this.api
      .reativar(usuario.usuarioUuid)
      .pipe(
        finalize(() => {
          this.excluindo = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: () => {
          void Swal.fire({
            icon: 'success',
            title: 'Usuário reativado!',
            confirmButtonColor: '#064F82',
          });

          this.carregar();
        },
        error: (error: HttpErrorResponse) => {
          void Swal.fire({
            icon: 'error',
            title: 'Não foi possível reativar',
            text: this.mensagemErro(error, 'Verifique a validade do plano e o limite de usuários.'),
            confirmButtonColor: '#064F82',
          });
        },
      });
  }
}
